import { z } from 'zod';

// Step Management Types
export type ImportStep = 'form' | 'preview' | 'progress';

export interface StepNavigationState {
  currentStep: ImportStep;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

// Form Data Types
export interface ImportFormData {
  providerId: number;
  versionId: number;
  format: 'name_first' | 'number_first';
  data: string;
  categoryId?: number;
  price?: number;
  description?: string;
  parentCategoryId?: number;
  subCategoryId?: number;
  planIds: number[];
  lastNumber?: string;
  typeId?: number;
  nextAvailableNumber?: string;
  is_local?: boolean;
  is_duplicate?: boolean;
}

// Channel Data Types
export interface ParsedChannelNumber {
  typeId: number;
  number: string | null;
  isSequence: boolean;
  is_default: boolean;
}

export interface ParsedChannelData {
  name: string;
  line: number;
  numbers: ParsedChannelNumber[];
  planIds: number[];
  categoryId?: number;
  price?: number;
  description?: string;
  error?: {
    message: string;
    type: string;
    field: string;
  };
}

// Validation Schemas
export const sequentialImportSchema = z.object({
  providerId: z.number(),
  versionId: z.number(),
  data: z.string()
    .min(1, 'Channel names are required')
    .refine(data => data.trim().length > 0, 'Import data cannot be empty'),
  categoryId: z.number().optional(),
  parentCategoryId: z.number().optional(),
  subCategoryId: z.number().optional(),
  price: z.number().optional(),
  description: z.string().optional(),
  planIds: z.array(z.number()).min(1, 'At least one plan must be selected'),
  typeId: z.number().optional(),
  is_local: z.boolean().optional(),
  is_duplicate: z.boolean().optional()
});

export const nonSequentialImportSchema = z.object({
  providerId: z.number(),
  versionId: z.number(),
  format: z.enum(['name_first', 'number_first']),
  data: z.string()
    .min(1, 'Import data is required')
    .refine(data => data.trim().length > 0, 'Import data cannot be empty'),
  categoryId: z.number().optional(),
  parentCategoryId: z.number().optional(),
  subCategoryId: z.number().optional(),
  price: z.number().optional(),
  description: z.string().optional(),
  planIds: z.array(z.number()).min(1, 'At least one plan must be selected'),
  typeId: z.number().optional(),
  is_local: z.boolean().optional(),
  is_duplicate: z.boolean().optional()
});

// Error Types
export interface ImportError {
  line: number;
  message: string;
  type: 'validation' | 'process' | 'preview' | 'network';
  field?: string;
  code?: string;
}

// Add validation error types
export interface ValidationError {
  type: 'validation';
  message: string;
  field: string;
  line: number;
}

// Add process error types
export interface ProcessError {
  type: 'process';
  message: string;
  code: string;
  line: number;
}

// Import Session Types
export type ImportStatus = 
  | 'pending'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export interface ImportSession {
  id: number;
  status: ImportStatus;
  total_items: number;
  processed_items: number;
  error_log: ImportError[];
}

// Import Progress Types
export interface ImportProgress {
  total: number;
  processed: number;
  success: number;
  failed: number;
  errors: ImportError[];
}

// Import Result Types
export interface ImportResult {
  sessionId: number;
  success: ParsedChannelData[];
  failures: Array<{
    data: ParsedChannelData;
    error: ImportError;
  }>;
  progress: ImportProgress;
}

// Validation Result Types
export interface ValidationResult {
  isValid: boolean;
  errors: ImportError[];
  data?: ParsedChannelData[];
  warnings?: ImportError[];
}

// Number Validation Utils
export const channelNumberValidation = {
  validateFormat: (number: string, isSequential: boolean): boolean => {
    if (isSequential) return true;
    if (number === '*' || number === '') return true;
    return /^[^\x00-\x1F\x7F]+$/.test(number);
  },
  
  validateUnique: (numbers: string[], type: string): string[] => {
    const duplicates = numbers.reduce((acc: string[], num, i) => {
      if (num === '*' || num === '') return acc;
      if (numbers.indexOf(num) !== i) {
        acc.push(`Duplicate ${type} number: ${num}`);
      }
      return acc;
    }, []);
    return duplicates;
  }
};

// Add or update these types
export interface ImportState {
  currentStep: ImportStep;
  formData: Partial<ImportFormData>;
  previewData: ParsedChannelData[] | null;
  errors: ImportError[];
  isImporting: boolean;
  currentSession: ImportSession | null;
  startImport: () => Promise<void>;
}

// Add this with the other interfaces
export interface ImportOptions {
  batchSize?: number;
  maxDataSize?: number;  // Maximum number of records
  onProgress?: (progress: ImportProgress) => void;
  retryAttempts?: number;
  retryDelay?: number;
  providerId: number;
  versionId: number;
  categoryId: number;
  planIds: number[];
  is_local?: boolean; // Add local channel support
  is_duplicate?: boolean; // Add duplicate channel support
}

export interface ChannelData {
  name: string;
  description?: string;
  categoryId?: number;
  providerId: number;
  versionId: number;
  numbers?: Array<{
    typeId: number;
    number: string;
    isSequence?: boolean;
  }>;
  planIds?: number[];
}
